import {
  PortfolioBadge,
  PortfolioBucket,
  PortfolioImportance,
  SORT_OPTION
} from 'enums';
import {
  PORTFOLIO_ENCOURAGED_START_NOW_LIMIT,
  PORTFOLIO_NO_RISK_RANK,
  RECOMMENDED_PACE_ROUND_MINUTES,
  RECOMMENDED_TODAY_VISIBLE_LIMIT,
  TOP_SCORE_TIER
} from 'constants/project';
import {
  PaceDurationParts,
  PortfolioActionGroups,
  PortfolioProjectRisk,
  PortfolioResponse,
  Project,
  RecommendedTodayItem,
  RecommendedTodayPlan
} from 'interfaces/project.interface';
import {
  ITEM_INDEX_NOT_FOUND,
  TIME_UNIT_CONVERT_FACTOR
} from 'constants/general';
import { hasAnyItems, hasItems, sortBy, take } from 'utils/array.util';

export const IMPORTANCE_OPTIONS: PortfolioImportance[] = [
  PortfolioImportance.LOW,
  PortfolioImportance.MEDIUM,
  PortfolioImportance.HIGH,
  PortfolioImportance.CRITICAL
];

export const DEFAULT_PORTFOLIO_IMPORTANCE = PortfolioImportance.MEDIUM;

interface BucketStyle {
  badge: string;
  dot: string;
}

const BUCKET_STYLES: Record<PortfolioBucket, BucketStyle> = {
  [PortfolioBucket.ON_TRACK]: {
    badge:
      'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    dot: 'bg-emerald-500'
  },
  [PortfolioBucket.AT_RISK]: {
    badge:
      'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    dot: 'bg-amber-500'
  },
  [PortfolioBucket.CRITICAL]: {
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    dot: 'bg-red-500'
  },
  [PortfolioBucket.NO_DEADLINE]: {
    badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    dot: 'bg-gray-400'
  }
};

const BUCKET_LABEL_KEYS: Record<PortfolioBucket, string> = {
  [PortfolioBucket.ON_TRACK]: 'portfolio.bucket_on_track',
  [PortfolioBucket.AT_RISK]: 'portfolio.bucket_at_risk',
  [PortfolioBucket.CRITICAL]: 'portfolio.bucket_critical',
  [PortfolioBucket.NO_DEADLINE]: 'portfolio.bucket_no_deadline'
};

const IMPORTANCE_LABEL_KEYS: Record<PortfolioImportance, string> = {
  [PortfolioImportance.LOW]: 'portfolio.importance_low',
  [PortfolioImportance.MEDIUM]: 'portfolio.importance_medium',
  [PortfolioImportance.HIGH]: 'portfolio.importance_high',
  [PortfolioImportance.CRITICAL]: 'portfolio.importance_critical'
};

const BADGE_LABEL_KEYS: Record<PortfolioBadge, string> = {
  [PortfolioBadge.OVERCOMMITTED]: 'portfolio.badge_overcommitted',
  [PortfolioBadge.START_NOW]: 'portfolio.badge_start_now',
  [PortfolioBadge.SACRIFICE]: 'portfolio.badge_sacrifice'
};

type MetaChipClassName = string;

const BADGE_META_STYLES: Record<PortfolioBadge, MetaChipClassName> = {
  [PortfolioBadge.OVERCOMMITTED]:
    'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  [PortfolioBadge.START_NOW]:
    'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
  [PortfolioBadge.SACRIFICE]:
    'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
};

const STATS_META_CLASS: MetaChipClassName =
  'bg-slate-50 text-slate-600 dark:bg-secondary-dark-400 dark:text-secondary-dark-900';

const DUE_META_STYLES: Record<PortfolioBucket, MetaChipClassName> = {
  [PortfolioBucket.ON_TRACK]:
    'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  [PortfolioBucket.AT_RISK]:
    'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  [PortfolioBucket.CRITICAL]:
    'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  [PortfolioBucket.NO_DEADLINE]:
    'bg-gray-100 text-gray-600 dark:bg-secondary-dark-400 dark:text-secondary-dark-900'
};

const HOURS_META_CLASS =
  'bg-slate-50 text-slate-600 dark:bg-secondary-dark-400 dark:text-secondary-dark-900';

const IMPORTANCE_META_STYLES: Record<PortfolioImportance, MetaChipClassName> = {
  [PortfolioImportance.LOW]:
    'bg-gray-100 text-gray-600 dark:bg-secondary-dark-400 dark:text-secondary-dark-900',
  [PortfolioImportance.MEDIUM]:
    'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  [PortfolioImportance.HIGH]:
    'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  [PortfolioImportance.CRITICAL]:
    'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
};

export const getDueMetaStyle = (bucket: PortfolioBucket): MetaChipClassName =>
  DUE_META_STYLES[bucket];

export const getHoursMetaStyle = (): MetaChipClassName => HOURS_META_CLASS;

const RECOMMENDED_PACE_META_CLASS: MetaChipClassName =
  'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300';

export const getRecommendedPaceMetaStyle = (): MetaChipClassName =>
  RECOMMENDED_PACE_META_CLASS;

export const getRecommendedDailyHours = (
  project: PortfolioProjectRisk
): number | null => {
  const { required_hours, days_until_due } = project;
  if (required_hours <= 0 || days_until_due === null) {
    return null;
  }
  // Due today/overdue: the full remaining effort is today's recommended load.
  if (days_until_due <= 0) {
    return required_hours;
  }
  return required_hours / days_until_due;
};

const toRecommendedTodayItem = (
  project: PortfolioProjectRisk
): RecommendedTodayItem | null => {
  const dailyHours = getRecommendedDailyHours(project);
  if (dailyHours === null) {
    return null;
  }
  return { id: project.id, name: project.name, dailyHours };
};

/** Build today's pace plan from Start-now order; cap visible rows. */
export const buildRecommendedTodayPlan = (
  projects: PortfolioProjectRisk[],
  visibleLimit: number = RECOMMENDED_TODAY_VISIBLE_LIMIT
): RecommendedTodayPlan => {
  const items = projects
    .map(toRecommendedTodayItem)
    .filter((item): item is RecommendedTodayItem => item !== null);

  const totalDailyHours = items.reduce(
    (total, item) => total + item.dailyHours,
    0
  );
  const visibleItems = take(items, visibleLimit);
  const hiddenCount = Math.max(0, items.length - visibleItems.length);

  return { visibleItems, totalDailyHours, hiddenCount };
};

export const roundHoursToDurationParts = (hours: number): PaceDurationParts => {
  if (hours <= 0) {
    return { hours: 0, minutes: 0 };
  }
  const minutesPerHour = TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_MINUTES;
  const rawMinutes = hours * minutesPerHour;
  const roundedMinutes = Math.max(
    RECOMMENDED_PACE_ROUND_MINUTES,
    Math.round(rawMinutes / RECOMMENDED_PACE_ROUND_MINUTES) *
      RECOMMENDED_PACE_ROUND_MINUTES
  );
  return {
    hours: Math.floor(roundedMinutes / minutesPerHour),
    minutes: roundedMinutes % minutesPerHour
  };
};

export const getImportanceMetaStyle = (
  importance: PortfolioImportance
): MetaChipClassName =>
  IMPORTANCE_META_STYLES[importance] ??
  IMPORTANCE_META_STYLES[PortfolioImportance.MEDIUM];

export const getBucketBadgeClass = (bucket: PortfolioBucket): string =>
  BUCKET_STYLES[bucket].badge;

export const getBucketDotClass = (bucket: PortfolioBucket): string =>
  BUCKET_STYLES[bucket].dot;

export const getBucketLabelKey = (bucket: PortfolioBucket): string =>
  BUCKET_LABEL_KEYS[bucket];

export const getImportanceLabelKey = (
  importance: PortfolioImportance
): string =>
  IMPORTANCE_LABEL_KEYS[importance] ??
  IMPORTANCE_LABEL_KEYS[PortfolioImportance.MEDIUM];

const BADGE_DISPLAY_ORDER: Record<PortfolioBadge, number> = {
  [PortfolioBadge.START_NOW]: 0,
  [PortfolioBadge.OVERCOMMITTED]: 1,
  [PortfolioBadge.SACRIFICE]: 2
};

export const getBadgeLabelKey = (badge: PortfolioBadge): string =>
  BADGE_LABEL_KEYS[badge];

export const getBadgeMetaStyle = (badge: PortfolioBadge): MetaChipClassName =>
  BADGE_META_STYLES[badge];

export const getStatsMetaStyle = (): MetaChipClassName => STATS_META_CLASS;

export const getTopScoreMetaStyle = (score: number): MetaChipClassName => {
  if (score >= TOP_SCORE_TIER.HIGH) {
    return 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  }
  if (score >= TOP_SCORE_TIER.MEDIUM) {
    return 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300';
  }
  return 'bg-slate-50 text-slate-600 dark:bg-secondary-dark-400 dark:text-secondary-dark-900';
};

/** Stable chip order: Start now → Overcommitted → Sacrifice. */
export const sortPortfolioBadges = (
  badges: PortfolioBadge[]
): PortfolioBadge[] =>
  [...badges].sort(
    (badgeA, badgeB) =>
      BADGE_DISPLAY_ORDER[badgeA] - BADGE_DISPLAY_ORDER[badgeB]
  );

export const getRiskRank = (risk: PortfolioProjectRisk | undefined): number => {
  if (!risk || risk.bucket === PortfolioBucket.NO_DEADLINE) {
    return PORTFOLIO_NO_RISK_RANK;
  }
  return risk.risk_score;
};

export const sortProjectsByRisk = (
  projects: Project[],
  riskByProjectId: Map<string, PortfolioProjectRisk>
): Project[] =>
  [...projects].sort(
    (a, b) =>
      getRiskRank(riskByProjectId.get(b.id)) -
      getRiskRank(riskByProjectId.get(a.id))
  );

export const buildRiskByProjectId = (
  portfolioProjects: PortfolioProjectRisk[]
): Map<string, PortfolioProjectRisk> =>
  new Map(portfolioProjects.map((project) => [project.id, project]));

/**
 * Action-section grouping (this-week panel buckets).
 *
 * When start-now would otherwise be empty, we promote a few due-dated
 * ON_TRACK projects so students build buffer against deadlines — not to
 * surface the most important work.
 *
 * Prefer time pressure when it exists.
 * If there’s no time pressure at all, fall back to importance so the
 * panel still guides action.
 *
 * PSEUDOCODE:
 * 1. Split each project into exactly one section:
 *    - CRITICAL bucket                         → askForExtension
 *    - AT_RISK bucket OR has START_NOW badge    → startNow
 *    - ON_TRACK | NO_DEADLINE                   → backBurner
 *    - (no omit for known portfolio buckets)
 * 2. If startNow is NOT empty → return sorted sections (no promotion).
 * 3. If startNow IS empty:
 *    a. dueDated = ON_TRACK backBurner with due_date !== null
 *       → sort soonest due_date, then higher importance
 *       → take up to PORTFOLIO_ENCOURAGED_START_NOW_LIMIT
 *       → move those into startNow (remove from backBurner)
 *    b. else (no due-dated candidates):
 *       → HIGH/CRITICAL importance backBurner projects
 *       → sort importance descending
 *       → take up to PORTFOLIO_ENCOURAGED_START_NOW_LIMIT
 *       → move those into startNow (remove from backBurner)
 * 4. Sort askForExtension by risk_score desc;
 *    startNow by overdue first then risk_score desc;
 *    backBurner by due-date present → risk_score desc → importance desc.
 * 5. UI (ThisWeekPanel): take(..., PORTFOLIO_START_NOW_VISIBLE_LIMIT)
 *    and take(..., PORTFOLIO_BACK_BURNER_VISIBLE_LIMIT)
 *    (hard caps; grouping still returns full remaining lists).
 */
const isBackBurnerBucket = (bucket: PortfolioBucket): boolean =>
  bucket === PortfolioBucket.ON_TRACK || bucket === PortfolioBucket.NO_DEADLINE;

const isStartNowSectionProject = (project: PortfolioProjectRisk): boolean =>
  project.bucket === PortfolioBucket.AT_RISK ||
  (project.badges ?? []).includes(PortfolioBadge.START_NOW);

const byDueDateSoonestThenImportance = (
  projectA: PortfolioProjectRisk,
  projectB: PortfolioProjectRisk
): number => {
  const dueDateA = projectA.due_date as string;
  const dueDateB = projectB.due_date as string;
  if (dueDateA !== dueDateB) {
    return dueDateA < dueDateB ? -1 : 1;
  }
  return projectB.importance - projectA.importance;
};

const byImportanceDescending = (
  projectA: PortfolioProjectRisk,
  projectB: PortfolioProjectRisk
): number => projectB.importance - projectA.importance;

const isOverdue = (project: PortfolioProjectRisk): boolean =>
  project.days_until_due !== null && project.days_until_due < 0;

/** Overdue first, then higher risk. */
const byStartNowPriority = (
  projectA: PortfolioProjectRisk,
  projectB: PortfolioProjectRisk
): number => {
  const projectAIsOverdue = isOverdue(projectA);
  const projectBIsOverdue = isOverdue(projectB);
  if (projectAIsOverdue !== projectBIsOverdue) {
    return projectAIsOverdue ? -1 : 1;
  }
  return projectB.risk_score - projectA.risk_score;
};

const hasDueDate = (project: PortfolioProjectRisk): boolean =>
  project.due_date !== null;

/** Due-dated first, then higher risk, then higher importance. */
const byBackBurnerPriority = (
  projectA: PortfolioProjectRisk,
  projectB: PortfolioProjectRisk
): number => {
  const projectAHasDueDate = hasDueDate(projectA);
  const projectBHasDueDate = hasDueDate(projectB);
  if (projectAHasDueDate !== projectBHasDueDate) {
    return projectAHasDueDate ? -1 : 1;
  }
  if (projectA.risk_score !== projectB.risk_score) {
    return projectB.risk_score - projectA.risk_score;
  }
  return projectB.importance - projectA.importance;
};

const isDueDatedOnTrackCandidate = (project: PortfolioProjectRisk): boolean =>
  project.bucket === PortfolioBucket.ON_TRACK && project.due_date !== null;

const isHighOrCriticalImportance = (project: PortfolioProjectRisk): boolean =>
  project.importance === PortfolioImportance.HIGH ||
  project.importance === PortfolioImportance.CRITICAL;

const sortActionGroups = (
  groups: PortfolioActionGroups
): PortfolioActionGroups => ({
  askForExtension: sortBy(
    groups.askForExtension,
    (project) => project.risk_score,
    SORT_OPTION.DESC
  ),
  startNow: [...groups.startNow].sort(byStartNowPriority),
  backBurner: [...groups.backBurner].sort(byBackBurnerPriority)
});

const buildGroupsWithBackBurnerPromotion = ({
  askForExtension,
  backBurner
}: {
  readonly askForExtension: PortfolioProjectRisk[];
  readonly backBurner: PortfolioProjectRisk[];
}): PortfolioActionGroups => {
  const dueDatedEncouraged = take(
    backBurner
      .filter(isDueDatedOnTrackCandidate)
      .sort(byDueDateSoonestThenImportance),
    PORTFOLIO_ENCOURAGED_START_NOW_LIMIT
  );
  const encouragedStartNow = hasItems(dueDatedEncouraged)
    ? dueDatedEncouraged
    : take(
        backBurner
          .filter(isHighOrCriticalImportance)
          .sort(byImportanceDescending),
        PORTFOLIO_ENCOURAGED_START_NOW_LIMIT
      );
  const encouragedIds = new Set(
    encouragedStartNow.map((project) => project.id)
  );

  return {
    askForExtension: sortBy(
      askForExtension,
      (project) => project.risk_score,
      SORT_OPTION.DESC
    ),
    startNow: [...encouragedStartNow].sort(byStartNowPriority),
    backBurner: backBurner
      .filter((project) => !encouragedIds.has(project.id))
      .sort(byBackBurnerPriority)
  };
};

export const groupProjectsByActionSection = (
  projects: PortfolioProjectRisk[]
): PortfolioActionGroups => {
  const askForExtension: PortfolioProjectRisk[] = [];
  const startNow: PortfolioProjectRisk[] = [];
  const backBurner: PortfolioProjectRisk[] = [];

  projects.forEach((project) => {
    if (project.bucket === PortfolioBucket.CRITICAL) {
      askForExtension.push(project);
    } else if (isStartNowSectionProject(project)) {
      startNow.push(project);
    } else if (isBackBurnerBucket(project.bucket)) {
      backBurner.push(project);
    }
  });

  if (hasItems(startNow)) {
    return sortActionGroups({ askForExtension, startNow, backBurner });
  }

  return buildGroupsWithBackBurnerPromotion({ askForExtension, backBurner });
};

export const hasActionSectionContent = (
  groups: PortfolioActionGroups
): boolean =>
  hasAnyItems(groups.askForExtension, groups.startNow, groups.backBurner);

export const formatOverCommittedProjectNames = (
  projects: PortfolioProjectRisk[],
  nameLimit: number,
  andMoreLabel: (count: number) => string
): string => {
  if (!hasItems(projects)) {
    return '';
  }

  const names = projects.map((project) => project.name);
  if (names.length <= nameLimit) {
    return names.join(', ');
  }

  const visibleNames = take(names, nameLimit).join(', ');
  const remainingCount = names.length - nameLimit;
  return `${visibleNames}, ${andMoreLabel(remainingCount)}`;
};

export const patchPortfolioProjectFromUpdate = (
  portfolio: PortfolioResponse,
  updatedProject: Project
): PortfolioResponse => {
  const projectIndex = portfolio.projects.findIndex(
    (project) => project.id === updatedProject.id
  );
  if (projectIndex === ITEM_INDEX_NOT_FOUND) {
    return portfolio;
  }

  const existingProject = portfolio.projects[projectIndex];
  const patchedProjects = [...portfolio.projects];
  patchedProjects[projectIndex] = {
    ...existingProject,
    name: updatedProject.name,
    due_date: updatedProject.due_date ?? null,
    importance: updatedProject.importance ?? existingProject.importance
  };

  return {
    ...portfolio,
    projects: patchedProjects
  };
};
